import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { DefaultParams, PathPattern, Route, Switch, useLocation } from 'wouter'
import Footer from './components/footer'
import { Header } from './components/header'
import { Loading } from './components/loading'
import { Padding } from './components/padding'
import { QuoteDisplay } from './components/QuoteDisplay'
import { TOCHeader } from './components/toc_header'
import useTableOfContents from './hooks/useTableOfContents.tsx'
import { client } from './main'
import { FeedsPage } from './page/feeds'
import { ClientConfigContext, ConfigWrapper, defaultClientConfig } from './state/config.tsx'
import { Profile, ProfileContext } from './state/profile'
import { headersWithAuth } from './utils/auth'
import { tryInt } from './utils/int'
import { Tips, TipsPage } from './components/tips.tsx'
import { useTranslation } from 'react-i18next'
import MusicPlayer from './components/MusicPlayer'
import { Seo } from './components/seo'

const CallbackPage = lazy(() => import('./page/callback').then((m) => ({ default: m.CallbackPage })))
const FeedPage = lazy(() => import('./page/feed').then((m) => ({ default: m.FeedPage })))
const FriendsPage = lazy(() => import('./page/friends').then((m) => ({ default: m.FriendsPage })))
const GamePage = lazy(() => import('./page/game').then((m) => ({ default: m.GamePage })))
const GameDetailPage = lazy(() => import('./page/game-detail').then((m) => ({ default: m.GameDetailPage })))
const HashtagPage = lazy(() => import('./page/hashtag').then((m) => ({ default: m.HashtagPage })))
const HashtagsPage = lazy(() => import('./page/hashtags').then((m) => ({ default: m.HashtagsPage })))
const SearchPage = lazy(() => import('./page/search').then((m) => ({ default: m.SearchPage })))
const Settings = lazy(() => import('./page/settings').then((m) => ({ default: m.Settings })))
const TimelinePage = lazy(() => import('./page/timeline').then((m) => ({ default: m.TimelinePage })))
const ToolboxPage = lazy(() => import('./page/toolbox').then((m) => ({ default: m.ToolboxPage })))
const ToolboxToolPage = lazy(() => import('./page/toolbox-tool').then((m) => ({ default: m.ToolboxToolPage })))
const WritingPage = lazy(() => import('./page/writing').then((m) => ({ default: m.WritingPage })))

function App() {
  const ref = useRef(false)
  const { t } = useTranslation()
  const [location] = useLocation()
  const [profile, setProfile] = useState<Profile | undefined>()
  const [config, setConfig] = useState<ConfigWrapper>(new ConfigWrapper({}, new Map()))
  useEffect(() => {
    if (ref.current) return
    const config = sessionStorage.getItem('config')
    if (config) {
      const configObj = JSON.parse(config)
      const configWrapper = new ConfigWrapper(configObj, defaultClientConfig)
      setConfig(configWrapper)
    } else {
      client.config({ type: "client" }).get().then(({ data }) => {
        if (data && typeof data !== 'string') {
          sessionStorage.setItem('config', JSON.stringify(data))
          const config = new ConfigWrapper(data, defaultClientConfig)
          setConfig(config)
        }
      })
    }
    ref.current = true
  }, [])

  useEffect(() => {
    let active = true

    client.user.profile.get({
      headers: headersWithAuth()
    }).then(({ data }) => {
      if (!active) {
        return
      }

      if (data && typeof data !== 'string') {
        setProfile({
          id: data.id,
          avatar: data.avatar || '',
          permission: data.permission,
          name: data.username
        })
        return
      }

      setProfile(undefined)
    }).catch((error) => {
      console.error(error)
      if (active) {
        setProfile(undefined)
      }
    })

    return () => {
      active = false
    }
  }, [location])

  const favicon = `${process.env.API_URL}/favicon`;
  return (
    <>
      <ClientConfigContext.Provider value={config}>
        <ProfileContext.Provider value={profile}>
          <Helmet>
            {favicon &&
              <link rel="icon" href={favicon} />}
          </Helmet>
          <Switch>
            <RouteMe path="/">
              <FeedsPage />
            </RouteMe>

            <RouteMe path="/timeline">
              <TimelinePage />
            </RouteMe>


            <RouteMe path="/friends">
              <FriendsPage />
            </RouteMe>

            <RouteMe path="/hashtags">
              <HashtagsPage />
            </RouteMe>

            <RouteMe path="/game">
              <RouteRedirect to="/games" />
            </RouteMe>

            <RouteMe path="/game/:id">
              {params => {
                return (<RouteRedirect to={`/games/${params.id || ""}`} />)
              }}
            </RouteMe>

            <RouteMe path="/games">
              <GamePage />
            </RouteMe>

            <RouteMe path="/games/:id">
              {params => {
                return (<GameDetailPage gameId={params.id || ""} />)
              }}
            </RouteMe>

            <RouteMe path="/tools/:alias">
              {params => {
                return (<ToolboxToolPage alias={params.alias || ""} />)
              }}
            </RouteMe>

            <RouteMe path="/toolbox/:alias">
              {params => {
                return (<ToolboxToolPage alias={params.alias || ""} />)
              }}
            </RouteMe>

            <RouteMe path="/tools">
              <ToolboxPage />
            </RouteMe>

            <RouteMe path="/toolbox">
              <ToolboxPage />
            </RouteMe>

            <RouteMe path="/hashtag/:name">
              {params => {
                return (<HashtagPage name={params.name || ""} />)
              }}
            </RouteMe>

            <RouteMe path="/search/:keyword">
              {params => {
                return (<SearchPage keyword={params.keyword || ""} />)
              }}
            </RouteMe>

            <RouteMe path="/settings" paddingClassName='mx-4'>
              <Settings />
            </RouteMe>


            <RouteMe path="/writing" paddingClassName='mx-4'>
              <WritingPage />
            </RouteMe>

            <RouteMe path="/writing/:id" paddingClassName='mx-4'>
              {({ id }) => {
                const id_num = tryInt(0, id)
                return (
                  <WritingPage id={id_num} />
                )
              }}
            </RouteMe>

            <RouteMe path="/callback" >
              <CallbackPage />
            </RouteMe>

            <RouteWithIndex path="/feed/:id">
              {(params, TOC, clean) => {
                return (<FeedPage id={params.id || ""} TOC={TOC} clean={clean} />)
              }}
            </RouteWithIndex>

            <RouteWithIndex path="/:alias">
              {(params, TOC, clean) => {
                return (
                  <FeedPage id={params.alias || ""} TOC={TOC} clean={clean} />
                )
              }}
            </RouteWithIndex>

            <RouteMe path="/user/github">
              {_ => (
                <TipsPage>
                  <Tips value={t('error.api_url')} type='error' />
                </TipsPage>
              )}
            </RouteMe>

            <RouteMe path="/*/user/github">
              {_ => (
                <TipsPage>
                  <Tips value={t('error.api_url_slash')} type='error' />
                </TipsPage>
              )}
            </RouteMe>

            <RouteMe path="/user/github/callback">
              {_ => (
                <TipsPage>
                  <Tips value={t('error.github_callback')} type='error' />
                </TipsPage>
              )}
            </RouteMe>

            {/* Default route in a switch */}
            <Route>
              <Seo title="404 Not Found" description="The requested page could not be found." noindex={true} />
              <div className="w-full flex justify-center py-16">
                <div className="flex flex-col rounded-2xl bg-w m-2 p-6 items-center justify-center space-y-2">
                  <h1 className="text-xl font-bold t-primary">404: No such page!</h1>
                </div>
              </div>
            </Route>
          </Switch>
          <MusicPlayer />
        </ProfileContext.Provider>
      </ClientConfigContext.Provider>
    </>
  )
}

function RouteMe({ path, children, headerComponent, paddingClassName }:
  { path: PathPattern, children: React.ReactNode | ((params: DefaultParams) => React.ReactNode), headerComponent?: React.ReactNode, paddingClassName?: string }) {
  return (
    <Route path={path} >
      {params => {
        return (<>
          <Header>
            {headerComponent}
          </Header>
          <QuoteDisplay />
          <Padding className={paddingClassName}>
            <Suspense
              fallback={
                <div className="flex justify-center items-center py-20 min-h-[40vh]">
                  <Loading />
                </div>
              }
            >
              {typeof children === 'function' ? children(params) : children}
            </Suspense>
          </Padding>
          <Footer />
        </>)
      }}
    </Route>
  )
}


function RouteWithIndex({ path, children }:
  { path: PathPattern, children: (params: DefaultParams, TOC: () => JSX.Element, clean: (id: string) => void) => React.ReactNode }) {
  const { TOC, cleanup } = useTableOfContents(".toc-content");
  return (<RouteMe path={path} headerComponent={TOCHeader({ TOC: TOC })} paddingClassName='mx-4'>
    {params => {
      return children(params, TOC, cleanup)
    }}
  </RouteMe>)
}

function RouteRedirect({ to }: { to: string }) {
  const [, setLocation] = useLocation()

  useEffect(() => {
    setLocation(to)
  }, [setLocation, to])

  return null
}

export default App
